import React, { useState, useMemo, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import {
  Typography,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid, GridToolbarContainer, GridToolbarExport } from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Page, Text, View, Document, PDFViewer, Image } from "@react-pdf/renderer";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PaymentIcon from "@mui/icons-material/Payment";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PublishedWithChangesOutlinedIcon from "@mui/icons-material/PublishedWithChangesOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import DialogContentText from "@mui/material/DialogContentText";
import { useMaterialUIController } from "context";
import { puedeAccion, puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  FP,
  PageHeader,
  StatCard,
  fpDataGridSx,
  fpInputSx,
  RazonCancelacionField,
} from "components/financepro";
import { useApi, usePerfilSucursal, useSucursales } from "lib/api";
import { imprimirFacturaMotor } from "modules/facturacion/impresionfacturamotor";
import { imprimirHtml, descargarB64 } from "modules/documentacion/documentos";
import { useMultiempresa, SelectorEmpresa, nombreEmpresa, useLogoEmpresa } from "lib/multiempresa";

function formatFechaFacturados(value) {
  if (!value) return "";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      const parts = String(value).split("-");
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return String(value);
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return String(value);
  }
}

function formatMoneyCell(value) {
  if (value == null || value === "") return "0.00";
  return parseFloat(value).toFixed(2);
}

function FacturadosToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

function buildFilterOptions(data) {
  const marcasSet = new Set();
  const modelos = [];
  const coloresSet = new Set();
  const modeloKeys = new Set();

  (data || []).forEach((item) => {
    if (item.marca) marcasSet.add(item.marca);
    if (item.colore) coloresSet.add(item.colore);
    if (item.marca && item.modelo) {
      const key = `${item.marca}|${item.modelo}`;
      if (!modeloKeys.has(key)) {
        modeloKeys.add(key);
        modelos.push({ name: item.marca, modelo: item.modelo });
      }
    }
  });

  return {
    marcas: ["TODO", ...Array.from(marcasSet).sort()],
    modelos,
    colores: ["TODO", ...Array.from(coloresSet).sort()],
  };
}

function applyVehicleFilters(data, marca, modelo, colore) {
  let result = data || [];
  if (marca && marca !== "TODO") {
    result = result.filter((row) => row.marca === marca);
  }
  if (modelo && modelo !== "TODO") {
    result = result.filter((row) => row.modelo === modelo);
  }
  if (colore && colore !== "TODO") {
    result = result.filter((row) => row.colore === colore);
  }
  return result;
}

// Venta al contado: factura sin préstamo (noprest vacío o 0). Es la que puede
// generar documentos con plantillas de tipo "Venta al contado" (acto de venta,
// descargo...); las de crédito los generan desde el expediente/documentos con
// su préstamo.
function esVentaContado(row) {
  const noprest = String(row?.noprest ?? "").trim();
  return !noprest || noprest === "0";
}

// Factura anulada (filtro Estado = Anuladas/Todas): precio queda en 0 y no
// suma; el original viene de la bitácora en `precioanulado`.
function notaAnulada(row) {
  return [
    "Anulada",
    row.anuladopor && `por ${row.anuladopor}`,
    row.fechaanulado && `el ${row.fechaanulado}`,
    row.razonanula && `— ${row.razonanula}`,
  ]
    .filter(Boolean)
    .join(" ");
}

// Anular la factura vale para contado y para crédito, pero la venta a crédito
// exige el préstamo ya cancelado: mismo orden que el escritorio (ares3010
// cancela el préstamo y recién ahí ares2020 anula la factura). Devuelve el
// motivo por el que NO se puede, o "" si se puede. Sin `status` en la fila
// deja pasar: decide el backend, que rechaza con su propio mensaje.
function motivoNoAnulable(row) {
  if (row?.anulada) return "La factura ya está anulada";
  const noprest = String(row?.noprest ?? "").trim();
  if (!noprest || noprest === "0") return "";
  const status = String(row?.status ?? "").trim().toUpperCase();
  if (status === "A") return `Cancele primero el préstamo #${noprest}`;
  if (status === "I") return `El préstamo #${noprest} está marcado incobrable`;
  return "";
}

function buildFacturadosColumns(
  onCancelar,
  onCambiarChasis,
  onImprimir,
  onDocumentos,
  multiempresa,
  completivos
) {
  const moneyFmt = (v) => formatMoneyCell(v);
  return [
    { field: "id", headerName: "Préstamo #", width: 110, align: "center", headerAlign: "center" },
    // Multi-empresa: empresa de la factura (solo con el modo activo)
    ...(multiempresa && multiempresa.activo
      ? [
          {
            field: "empresa",
            headerName: "Empresa",
            width: 130,
            valueGetter: (value, row) => nombreEmpresa(multiempresa.empresas, row.empresa || 1),
          },
        ]
      : []),
    {
      field: "fecha",
      headerName: "Fecha",
      width: 115,
      align: "center",
      headerAlign: "center",
      valueGetter: (value) => formatFechaFacturados(value),
    },
    { field: "nombres", headerName: "Nombres", flex: 1, minWidth: 130 },
    { field: "apellidos", headerName: "Apellidos", flex: 1, minWidth: 130 },
    { field: "marca", headerName: "Marca", width: 120 },
    { field: "modelo", headerName: "Modelo", width: 130 },
    { field: "colore", headerName: "Color", width: 100 },
    { field: "chasis", headerName: "Chasis", width: 150 },
    {
      field: "inicial",
      headerName: "Inicial",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueGetter: (value) => moneyFmt(value),
    },
    // Completivos al inicial (varinicial.completivoinicial): lo cobrado del
    // inicial y lo que falta por cobrar; Inicial sigue siendo el pactado.
    ...(completivos
      ? [
          {
            field: "inicialpagado",
            headerName: "Inicial pagado",
            width: 120,
            align: "right",
            headerAlign: "right",
            valueGetter: (value) => moneyFmt(value),
          },
          {
            field: "completivo",
            headerName: "Completivo",
            width: 115,
            align: "right",
            headerAlign: "right",
            valueGetter: (value) => moneyFmt(value),
          },
        ]
      : []),
    {
      field: "precio",
      headerName: "Precio",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueGetter: (value) => moneyFmt(value),
      // Anulada: chip y el precio original tachado (no suma en el total).
      renderCell: ({ row, formattedValue, value }) =>
        row.anulada ? (
          <Tooltip title={notaAnulada(row)}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.6, justifyContent: "flex-end" }}
            >
              <Chip
                label="ANULADA"
                size="small"
                color="error"
                variant="outlined"
                sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700 }}
              />
              <Box
                component="span"
                sx={{ textDecoration: "line-through", color: "text.secondary" }}
              >
                {moneyFmt(row.precioanulado)}
              </Box>
            </Box>
          </Tooltip>
        ) : (
          formattedValue ?? value
        ),
    },
    {
      field: "financia",
      headerName: "Financiamiento",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueGetter: (value) => moneyFmt(value),
    },
    {
      field: "interesacobrar",
      headerName: "Interés",
      width: 115,
      align: "right",
      headerAlign: "right",
      valueGetter: (value) => moneyFmt(value),
    },
    ...(onCancelar || onCambiarChasis || onImprimir || onDocumentos
      ? [
          {
            field: "acciones",
            headerName: "Acciones",
            width:
              60 +
              [onCancelar, onCambiarChasis, onImprimir, onDocumentos].filter(Boolean).length * 35,
            sortable: false,
            filterable: false,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => {
              // Cancelar aplica a contado y a crédito; en crédito solo con el
              // préstamo ya cancelado (ver motivoNoAnulable). Cambiar el chasis
              // y reimprimir aplican a cualquier factura; los documentos con
              // plantilla, solo a las ventas al contado.
              const motivo = motivoNoAnulable(params.row);
              return (
                <>
                  {onImprimir && (
                    <Tooltip title="Reimprimir factura">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onImprimir(params.row)}
                      >
                        <PrintOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onDocumentos && esVentaContado(params.row) && (
                    <Tooltip title="Documentos de la venta (acto de venta...)">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onDocumentos(params.row)}
                      >
                        <DescriptionOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onCambiarChasis && (
                    <Tooltip title="Cambiar chasis">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onCambiarChasis(params.row)}
                      >
                        <PublishedWithChangesOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onCancelar && (
                    <Tooltip title={motivo || "Cancelar factura"}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={!!motivo}
                          onClick={() => onCancelar(params.row)}
                        >
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </>
              );
            },
          },
        ]
      : []),
  ];
}

/**
 * @param embedded  Renderiza sin PageHeader ni fondo propio para usarse como
 *                  listado dentro de otra pantalla (p.ej. Ventas). En este
 *                  modo carga automáticamente el mes en curso.
 * @param refreshToken  Al cambiar su valor se recarga el listado (la pantalla
 *                      contenedora lo incrementa tras registrar una venta).
 * @param tipoInicial   Valor inicial del filtro de tipo de venta: "" (Todas,
 *                      default), "Contado", "Credito" o "Refinanciamiento".
 */
function InformeMotoresFacturadosNuevo({ embedded = false, refreshToken = 0, tipoInicial = "" } = {}) {
  const hoyISO = () => new Date().toISOString().slice(0, 10);
  const inicioMesISO = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
  };
  const [fecha1, setFecha1] = useState(embedded ? inicioMesISO() : "");
  const [fecha2, setFecha2] = useState(embedded ? hoyISO() : "");
  const [nosucursal, setNosucursal] = useState("");
  const [tipoVenta, setTipoVenta] = useState(tipoInicial);
  // Estado: "vigentes" (por defecto), "anuladas" o "todas".
  const [estado, setEstado] = useState("vigentes");
  const [marca, setMarca] = useState("TODO");
  const [modelo, setModelo] = useState("TODO");
  const [colore, setColore] = useState("TODO");
  const [marcas, setMarcas] = useState(["TODO"]);
  const [modelos, setModelos] = useState([]);
  const [filteredModelo, setFilteredModelo] = useState(["TODO"]);
  const [colores, setColores] = useState(["TODO"]);
  const [rowsAll, setRowsAll] = useState([]);
  const [rows, setRows] = useState([]);
  // El backend avisa si el tenant usa completivos al inicial; con el flag
  // apagado no vienen inicialpagado/completivo y no se muestran.
  const [completivos, setCompletivos] = useState(false);
  const [open, setOpen] = useState(false);
  const [chasisnuevo, setChasisnuevo] = useState("");
  // Fila cuyo chasis se está cambiando (la abre el botón de la columna Acciones).
  const [chasisTarget, setChasisTarget] = useState(null);
  const [cambiandoChasis, setCambiandoChasis] = useState(false);
  const [presentarpdf, setPresentarpdf] = useState(false);
  const [loading, setLoading] = useState(false);

  const [controller] = useMaterialUIController();
  const { nombreNegocio, roleDetalles } = controller;
  const { post } = useApi();
  // Alcance de sucursal: lo conceden el tipo admin o el permiso global
  // `versucursales` (usePerfilSucursal), o el checkbox "Ver sucursal" del
  // programa de Ventas en el rol (flag versucursal). El backend fuerza lo
  // mismo en /api/informemotoresfacturados; esto lo refleja en el filtro.
  const { esadmin, sucursalPropia } = usePerfilSucursal();
  const veSucursales =
    esadmin ||
    ["ares2010", "ares2015", "informemotoresfacturados"].some((prog) =>
      puedeAccionPrograma(roleDetalles, prog, "versucursal")
    );
  // `todas` salta la restricción de useSucursales: el flag del rol también
  // debe liberar las opciones del selector, no solo el permiso global.
  const sucursal = useSucursales({ todas: veSucursales });
  // Multi-empresa: filtro y columna (solo con el modo activo)
  const multiempresa = useMultiempresa();
  const [empresaFiltro, setEmpresaFiltro] = useState("0");
  // Logo de la empresa filtrada (o la principal) para el encabezado del PDF.
  const logoEmpresa = useLogoEmpresa(empresaFiltro);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  
  const puedeBorrar = puedeAccion(roleDetalles, "facturacion", "borrar");
  // Mismo permiso que valida el backend en permisos.py para
  // /api/modificachasisincautados: informemotoresfacturados/modificar.
  const puedeCambiarChasis = puedeAccionPrograma(
    roleDetalles,
    "informemotoresfacturados",
    "modificar"
  );

  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [razonCancel, setRazonCancel] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);

  // Usuario sin alcance multi-sucursal: se preselecciona su sucursal en el
  // filtro (las opciones ya vienen limitadas a la suya por useSucursales).
  useEffect(() => {
    if (veSucursales || !sucursalPropia || !sucursal.length) return;
    const propia = sucursal.find(
      (s) =>
        String(s.label || "")
          .split("-")[0]
          .trim() === sucursalPropia
    );
    if (propia) setNosucursal((prev) => prev || propia);
  }, [veSucursales, sucursalPropia, sucursal]);

  // En modo embebido el listado carga solo (al montar y cuando la pantalla
  // contenedora incrementa refreshToken tras guardar una venta).
  useEffect(() => {
    if (embedded && fecha1 && fecha2) procesarrequerimiento();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, refreshToken]);

  function abrirCancelarFactura(row) {
    setCancelTarget(row);
    setRazonCancel("");
    setConfirmCancelOpen(true);
  }

  function abrirCambiarChasis(row) {
    setChasisTarget(row);
    setChasisnuevo("");
    setOpen(true);
  }

  // La factura se lee del backend en vez de armarse con la fila: la grilla no
  // trae la forma de pago. El endpoint comparte el permiso de este informe,
  // así que quien ve el listado siempre puede reimprimir.
  // Ref y no estado: las columnas están memoizadas y su closure se quedaría
  // con el valor de la primera renderización, dejando el guard sin efecto.
  const imprimiendo = useRef(false);

  async function reimprimirFactura(row) {
    if (imprimiendo.current) return;
    imprimiendo.current = true;
    try {
      const { status, data } = await post("/api/facturamotor_detalle", { secuencia: row.id });
      if (status === 200) imprimirFacturaMotor(data);
      else alert(data?.Error || "No se pudo obtener la factura");
    } catch (err) {
      alert(`Error de red: ${err.message}`);
    } finally {
      imprimiendo.current = false;
    }
  }

  // ---- Documentos de una venta al contado (plantillas tipo "contado") ----
  // Mismo patrón que el contrato de la factura x mayor: se elige la plantilla
  // y sale por impresión/PDF o como Word. El backend valida el permiso de
  // esta pantalla, no exige el programa `documentos` (editor de plantillas).
  const [documentosTarget, setDocumentosTarget] = useState(null);
  const [plantillasContado, setPlantillasContado] = useState([]);
  const [plantillaSel, setPlantillaSel] = useState("");
  const [cargandoPlantillas, setCargandoPlantillas] = useState(false);
  const [generando, setGenerando] = useState(""); // "" | "imprimir" | "word"

  async function abrirDocumentos(row) {
    setDocumentosTarget(row);
    setCargandoPlantillas(true);
    try {
      const { ok, data } = await post("/api/ventacontado_documento_plantillas", {
        secuencia: row.id,
      });
      if (!ok) {
        alert(data?.Error || "No se pudieron cargar las plantillas");
        setDocumentosTarget(null);
        return;
      }
      const lista = data.plantillas || [];
      setPlantillasContado(lista);
      // Con una sola plantilla no tiene sentido obligar a elegirla.
      setPlantillaSel(lista.length === 1 ? lista[0].id : "");
    } finally {
      setCargandoPlantillas(false);
    }
  }

  async function generarDocumento(modo) {
    if (!plantillaSel) {
      alert("Seleccione una plantilla");
      return;
    }
    setGenerando(modo);
    try {
      const { ok, data } = await post("/api/ventacontado_documento_generar", {
        id: plantillaSel,
        secuencia: documentosTarget.id,
      });
      if (!ok) {
        alert(data?.Error || "No se pudo generar el documento");
        return;
      }
      if (modo === "imprimir") {
        imprimirHtml(data.html || "", data.nombre);
      } else if (data.archivo_b64) {
        descargarB64(data.archivo_b64, data.nombre);
      } else {
        alert("No se pudo convertir a Word; use Imprimir / PDF");
      }
      setDocumentosTarget(null);
    } finally {
      setGenerando("");
    }
  }

  async function cancelarMotorFacturado() {
    if (!cancelTarget) return;
    try {
      const { status, data } = await post("/api/eliminarmotoresfacturados", {
        id: cancelTarget.id,
        razon: razonCancel,
      });
      if (status === 200) {
        setRowsAll((prev) => prev.filter((r) => r.id !== cancelTarget.id));
        setRows((prev) => prev.filter((r) => r.id !== cancelTarget.id));
        setConfirmCancelOpen(false);
        setCancelTarget(null);
        // El backend dice cuántos iniciales se llevó la factura: mostrarlo
        // evita que el cajero descubra el faltante cuando cuadre la caja.
        const anulados = Number(data?.inicialesanulados) || 0;
        alert(
          (anulados
            ? `Factura cancelada correctamente. Se anularon ${anulados} inicial(es) por ` +
              `${formatMoneyCell(data?.valoranulado)}.`
            : "Factura cancelada correctamente.") +
            // Contado: el backend devuelve el chasis al inventario.
            (data?.chasisreingresado ? " El chasis volvió al inventario." : "")
        );
      } else {
        alert(data?.Error || "No se pudo cancelar la factura");
        setConfirmCancelOpen(false);
      }
    } catch (err) {
      alert("Error de red: " + err.message);
      setConfirmCancelOpen(false);
    }
  }

  const totalInicial = rows.reduce((sum, row) => sum + (parseFloat(row.inicial) || 0), 0);
  const totalInicialPagado = rows.reduce((sum, row) => sum + (parseFloat(row.inicialpagado) || 0), 0);
  const totalCompletivo = rows.reduce((sum, row) => sum + (parseFloat(row.completivo) || 0), 0);
  const totalPrecio = rows.reduce((sum, row) => sum + (parseFloat(row.precio) || 0), 0);
  const totalFinanciamiento = rows.reduce((sum, row) => sum + (parseFloat(row.financia) || 0), 0);
  const totalInteres = rows.reduce((sum, row) => sum + (parseFloat(row.interesacobrar) || 0), 0);

  const facturadosColumns = useMemo(
    () =>
      buildFacturadosColumns(
        puedeBorrar ? abrirCancelarFactura : undefined,
        puedeCambiarChasis ? abrirCambiarChasis : undefined,
        reimprimirFactura,
        abrirDocumentos,
        multiempresa,
        completivos
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [puedeBorrar, puedeCambiarChasis, multiempresa, completivos]
  );

  async function procesarrequerimiento(event) {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }

    if (!fecha1 || !fecha2) {
      alert("Por favor seleccione ambas fechas");
      return;
    }

    setLoading(true);
    try {
      const { status, data } = await post("/api/informemotoresfacturados", {
        fechadesde: fecha1,
        fechahasta: fecha2,
        sucursal: nosucursal && nosucursal.label ? nosucursal.label.split("-")[0] : "",
        // Tipo de venta: "" (todas), "Contado", "Credito" o "Refinanciamiento".
        // `refinanciados` se sigue enviando por compatibilidad con backends
        // que aún no conocen `tipoventa`.
        tipoventa: tipoVenta,
        refinanciados: tipoVenta === "Refinanciamiento",
        estado,
        ...(multiempresa.activo ? { empresa: Number(empresaFiltro) } : {}),
      });

      if (status === 200) {
        const facturacion = data["facturacion"] || [];
        setCompletivos(data.completivoinicial === true);
        const options = buildFilterOptions(facturacion);
        setRowsAll(facturacion);
        setMarcas(options.marcas);
        setModelos(options.modelos);
        setColores(options.colores);
        const list = options.modelos.filter((item) => item.name === marca);
        const modelolist = list.map((item) => item.modelo);
        modelolist.unshift("TODO");
        setFilteredModelo(modelolist.length > 1 ? modelolist : ["TODO"]);
        setRows(applyVehicleFilters(facturacion, marca, modelo, colore));
        return;
      }

      setRowsAll([]);
      setRows([]);
      if (status === 400) {
        const msg = data["Error"] || "Solicitud inválida";
        // En modo embebido la carga es automática: un rango sin facturas no
        // debe interrumpir con un alert, la tabla vacía ya lo comunica.
        if (!(embedded && msg.startsWith("No hay facturas"))) alert(msg);
        return;
      }

      alert(data["Error"] || "Error al procesar la solicitud");
    } catch (error) {
      setRows([]);
      alert("Error al procesar la solicitud");
    } finally {
      setLoading(false);
    }
  }

  async function modificarchasis() {
    if (!chasisTarget) return;
    const nuevo = chasisnuevo.trim().toUpperCase();
    if (nuevo.length === 0) {
      alert("Indique el chasis nuevo");
      return;
    }
    setCambiandoChasis(true);
    try {
      const { status, data } = await post("/api/modificachasisincautados", {
        // La factura, no solo el chasis: un chasis vendido, cancelado y
        // revendido tiene varias facturas y el backend elegía una al azar.
        secuencia: chasisTarget.id,
        chasis: chasisTarget.chasis,
        chasisacambiar: nuevo,
      });
      if (status === 200) {
        setOpen(false);
        setChasisTarget(null);
        setChasisnuevo("");
        alert(data.OK || "Datos modificados correctamente");
        // El cambio toca marca/modelo/color de la factura: recargar el listado.
        procesarrequerimiento();
      } else {
        alert(data.Error || "No se pudo cambiar el chasis");
      }
    } catch (err) {
      alert(`Error de red: ${err.message}`);
    } finally {
      setCambiandoChasis(false);
    }
  }

  // Impresión con el mismo layout y datos del reporte del escritorio ares
  // ("Balance Prestamos"): la condición Nuevo/Usado va como (N)/(U) junto a
  // marca-modelo, COND. distingue Contado/Credito, y debajo del listado van
  // los totales, el resumen de ventas por condición, los datos de motores y
  // el conteo por marca. El estilo usa los tokens FP para que el impreso
  // combine con la app. `num` alinea la columna a la derecha.
  // Con completivos al inicial se agregan Pagado y Completivo junto al
  // inicial; para que quepan en el A4 apaisado se angostan las columnas de
  // texto (el segundo ancho de cada par).
  const anchoPdf = (normal, conCompletivos) => (completivos ? conCompletivos : normal);
  const PDF_COLUMNAS = [
    { clave: "pres", titulo: "Pres", ancho: 36 },
    { clave: "suc", titulo: "Suc.", ancho: anchoPdf(54, 44) },
    { clave: "nombres", titulo: "Nombres", ancho: anchoPdf(112, 74) },
    { clave: "chasis", titulo: "Chasis", ancho: anchoPdf(92, 80) },
    { clave: "marcamodelo", titulo: "Marca Modelo", ancho: anchoPdf(92, 78) },
    { clave: "color", titulo: "Color", ancho: anchoPdf(42, 38) },
    { clave: "fecha", titulo: "Fecha", ancho: anchoPdf(50, 46) },
    { clave: "cond", titulo: "Cond.", ancho: anchoPdf(44, 40) },
    { clave: "venta", titulo: "Venta", ancho: 56, num: true },
    { clave: "inicial", titulo: "Inicial", ancho: 52, num: true },
    ...(completivos
      ? [
          { clave: "pagado", titulo: "Pagado", ancho: 48, num: true },
          { clave: "completivo", titulo: "Complet.", ancho: 48, num: true },
        ]
      : []),
    { clave: "financ", titulo: "Financ", ancho: 56, num: true },
    { clave: "int", titulo: "Int %", ancho: 30, num: true },
    { clave: "cuota", titulo: "Cuota", ancho: anchoPdf(80, 68), num: true },
  ];

  const MyDocument = ({ rowsData, nombreNegocio: nombre }) => {
    const fmt = (v) =>
      (parseFloat(v) || 0).toLocaleString("es-DO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    const fdmy = (iso) => (iso ? String(iso).split("-").reverse().join("/") : "");
    const esContado = (r) => !r.noprest || String(r.noprest).trim() === "0";
    const esNuevo = (r) => (r.condicion || "").trim().toUpperCase().startsWith("N");
    const esUsado = (r) => (r.condicion || "").trim().toUpperCase().startsWith("U");

    const celdas = (r) => ({
      pres: String(esContado(r) ? r.id : r.noprest || ""),
      suc: r.descripcion || "",
      nombres: `${r.nombres || ""} ${r.apellidos || ""}`.trim(),
      chasis: r.chasis || "",
      marcamodelo: `${r.marca || ""} ${r.modelo || ""}${
        esNuevo(r) ? " (N)" : esUsado(r) ? " (U)" : ""
      }`.trim(),
      color: r.colore || "",
      fecha: r.fecha || "",
      cond: esContado(r) ? "Contado" : "Credito",
      venta: fmt(r.precio),
      inicial: fmt(r.inicial),
      pagado: fmt(r.inicialpagado),
      completivo: fmt(r.completivo),
      financ: fmt(r.financia),
      // prestamo.interes guarda la fracción por período; el escritorio la
      // imprime como porcentaje (0.0351 -> 3.51).
      int: ((parseFloat(r.interes) || 0) * 100).toFixed(2),
      cuota: esContado(r) ? "" : `${fmt(r.vcuotas)} (${parseInt(r.cuotas, 10) || 0} cuotas)`,
    });

    // Totales y resumen, con las mismas reglas del escritorio: la venta al
    // contado suma el precio de las facturas sin préstamo; iniciales y
    // financiamientos se desglosan por condición del motor (Nuevo/Usado).
    const suma = (campo) => rowsData.reduce((s, r) => s + (parseFloat(r[campo]) || 0), 0);
    const sumaSi = (cond, campo) =>
      rowsData.filter(cond).reduce((s, r) => s + (parseFloat(r[campo]) || 0), 0);
    const totalVenta = suma("precio");
    const totalInicial = suma("inicial");
    const totalPagado = suma("inicialpagado");
    const totalComplet = suma("completivo");
    const totalFinanc = suma("financia");
    const totalPagadoCapital = suma("vpagcap");
    const ventaContado = sumaSi(esContado, "precio");
    const inicialesUsados = sumaSi((r) => esUsado(r) && !esContado(r), "inicial");
    const financUsados = sumaSi((r) => esUsado(r) && !esContado(r), "financia");
    const inicialesNuevos = sumaSi((r) => esNuevo(r) && !esContado(r), "inicial");
    const financNuevos = sumaSi((r) => esNuevo(r) && !esContado(r), "financia");
    const motoresNuevos = rowsData.filter(esNuevo).length;
    const motoresUsados = rowsData.filter(esUsado).length;
    const porMarca = rowsData.reduce((acc, r) => {
      const m = (r.marca || "SIN MARCA").trim().toUpperCase() || "SIN MARCA";
      acc[m] = (acc[m] || 0) + 1;
      return acc;
    }, {});
    const generado = new Date().toLocaleString("es-DO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    const sucursalTexto = (nosucursal && nosucursal.label) || "Todas";
    const ESTADO_TEXTO = { anuladas: " (anuladas)", todas: " (incluye anuladas)" };
    const tipoTexto =
      (tipoVenta === "" ? "Todas" : tipoVenta === "Credito" ? "Crédito" : tipoVenta) +
      (ESTADO_TEXTO[estado] || "");

    const S = {
      pagina: {
        backgroundColor: "#FFFFFF",
        paddingTop: 24,
        paddingHorizontal: 24,
        paddingBottom: 42,
        color: FP.color.textPrimary,
      },
      encabezado: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end" },
      titulo: { fontSize: 16, fontFamily: "Helvetica-Bold", color: FP.color.navy, textTransform: "uppercase" },
      subtitulo: { fontSize: 9, color: FP.color.textSecondary, marginTop: 3, letterSpacing: 1.2, textTransform: "uppercase" },
      meta: { fontSize: 7, color: FP.color.textSecondary, textAlign: "right", lineHeight: 1.5 },
      metaValor: { fontFamily: "Helvetica-Bold", color: FP.color.navy },
      regla: { height: 3, backgroundColor: FP.color.navy, borderRadius: 2, marginTop: 8 },
      reglaAcento: { height: 1, backgroundColor: FP.color.accentBlue, marginTop: 1.5, marginBottom: 10 },
      thead: {
        flexDirection: "row",
        backgroundColor: FP.color.navy,
        color: "#FFFFFF",
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
        paddingVertical: 5,
        paddingHorizontal: 4,
        fontSize: 7,
        fontFamily: "Helvetica-Bold",
      },
      fila: {
        flexDirection: "row",
        paddingVertical: 3.5,
        paddingHorizontal: 4,
        fontSize: 6.8,
        borderBottomWidth: 1,
        borderBottomColor: FP.color.border,
      },
      filaTotal: {
        flexDirection: "row",
        paddingVertical: 5,
        paddingHorizontal: 4,
        fontSize: 7,
        fontFamily: "Helvetica-Bold",
        color: FP.color.navy,
        borderTopWidth: 1.5,
        borderTopColor: FP.color.navy,
        backgroundColor: FP.color.surfaceAlt,
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 4,
      },
      tarjetas: { flexDirection: "row", marginTop: 16 },
      tarjeta: {
        flex: 1,
        backgroundColor: FP.color.surface,
        borderWidth: 1,
        borderColor: FP.color.border,
        borderRadius: 6,
        padding: 10,
        marginRight: 10,
      },
      tarjetaUltima: { marginRight: 0 },
      tarjetaTitulo: {
        fontSize: 7.5,
        fontFamily: "Helvetica-Bold",
        color: FP.color.navy,
        letterSpacing: 1,
        textTransform: "uppercase",
        marginBottom: 7,
        paddingBottom: 4,
        borderBottomWidth: 1,
        borderBottomColor: FP.color.borderStrong,
      },
      dato: { flexDirection: "row", justifyContent: "space-between", marginBottom: 4 },
      datoEtiqueta: { fontSize: 7.5, color: FP.color.textSecondary },
      datoValor: { fontSize: 7.5, fontFamily: "Helvetica-Bold", color: FP.color.textPrimary },
      pieIzq: { position: "absolute", bottom: 16, left: 24, fontSize: 6.5, color: FP.color.textMuted },
      pieDer: { position: "absolute", bottom: 16, right: 24, fontSize: 6.5, color: FP.color.textMuted },
    };

    const celda = (col, extra) => ({
      width: col.ancho,
      textAlign: col.num ? "right" : "left",
      paddingRight: col.num ? 6 : 2,
      ...extra,
    });

    const lineaDato = (etiqueta, valor) => (
      <View key={etiqueta} style={S.dato}>
        <Text style={S.datoEtiqueta}>{etiqueta}</Text>
        <Text style={S.datoValor}>{valor}</Text>
      </View>
    );

    return (
      <PDFViewer style={{ height: isXs ? "calc(100vh - 120px)" : "550px", width: "100%", colorScheme: "light", background: "#fff" }}>
        <Document title={`Reporte de ventas ${fdmy(fecha1)} - ${fdmy(fecha2)}`}>
          <Page size="A4" orientation="landscape" style={S.pagina}>
            {/* Encabezado: empresa a la izquierda, parámetros del reporte a la
                derecha, separados del cuerpo por la doble regla navy/azul. */}
            <View fixed>
              <View style={S.encabezado}>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  {logoEmpresa ? (
                    <Image
                      src={logoEmpresa}
                      style={{ height: 36, width: 110, objectFit: "contain", marginRight: 10 }}
                    />
                  ) : null}
                  <View>
                    <Text style={S.titulo}>{nombre || "Empresa"}</Text>
                    <Text style={S.subtitulo}>Reporte de Ventas — Balance Prestamos</Text>
                  </View>
                </View>
                <View style={S.meta}>
                  <Text>
                    Período: <Text style={S.metaValor}>{`${fdmy(fecha1)} — ${fdmy(fecha2)}`}</Text>
                  </Text>
                  <Text>
                    Sucursal: <Text style={S.metaValor}>{sucursalTexto}</Text>
                    {"   ·   "}Tipo de venta: <Text style={S.metaValor}>{tipoTexto}</Text>
                  </Text>
                  <Text>
                    Facturas: <Text style={S.metaValor}>{String(rowsData.length)}</Text>
                  </Text>
                </View>
              </View>
              <View style={S.regla} />
              <View style={S.reglaAcento} />
            </View>

            <View style={S.thead} fixed>
              {PDF_COLUMNAS.map((col) => (
                <Text key={col.clave} style={celda(col)}>
                  {col.titulo}
                </Text>
              ))}
            </View>
            {rowsData.map((fila, i) => {
              const c = celdas(fila);
              return (
                <View
                  key={fila.id ?? i}
                  style={{ ...S.fila, backgroundColor: i % 2 === 0 ? "#FFFFFF" : FP.color.surface }}
                  wrap={false}
                >
                  {PDF_COLUMNAS.map((col) => (
                    <Text key={col.clave} style={celda(col)}>
                      {c[col.clave]}
                    </Text>
                  ))}
                </View>
              );
            })}
            <View style={S.filaTotal} wrap={false}>
              {PDF_COLUMNAS.map((col) => {
                if (col.clave === "nombres") return <Text key={col.clave} style={celda(col)}>TOTALES</Text>;
                if (col.clave === "venta") return <Text key={col.clave} style={celda(col)}>{fmt(totalVenta)}</Text>;
                if (col.clave === "inicial") return <Text key={col.clave} style={celda(col)}>{fmt(totalInicial)}</Text>;
                if (col.clave === "pagado") return <Text key={col.clave} style={celda(col)}>{fmt(totalPagado)}</Text>;
                if (col.clave === "completivo") return <Text key={col.clave} style={celda(col)}>{fmt(totalComplet)}</Text>;
                if (col.clave === "financ") return <Text key={col.clave} style={celda(col)}>{fmt(totalFinanc)}</Text>;
                return <Text key={col.clave} style={celda(col)}> </Text>;
              })}
            </View>

            {/* Resumen en tarjetas, con los mismos renglones del escritorio. */}
            <View style={S.tarjetas} wrap={false}>
              <View style={S.tarjeta}>
                <Text style={S.tarjetaTitulo}>Totales</Text>
                {lineaDato("Totales de chasis", fmt(rowsData.length))}
                {lineaDato("Total pagado capital", fmt(totalPagadoCapital))}
              </View>
              <View style={S.tarjeta}>
                <Text style={S.tarjetaTitulo}>Resumen de Ventas</Text>
                {lineaDato("Venta al contado", fmt(ventaContado))}
                {lineaDato("Iniciales usados", fmt(inicialesUsados))}
                {lineaDato("Financiamientos usados", fmt(financUsados))}
                {lineaDato("Iniciales nuevos", fmt(inicialesNuevos))}
                {lineaDato("Financiamientos nuevos", fmt(financNuevos))}
              </View>
              <View style={{ ...S.tarjeta, ...S.tarjetaUltima }}>
                <Text style={S.tarjetaTitulo}>Datos de Motores</Text>
                {lineaDato("Motores nuevos", String(motoresNuevos))}
                {lineaDato("Motores usados", String(motoresUsados))}
                {Object.entries(porMarca).map(([m, cant]) => lineaDato(m, String(cant)))}
              </View>
            </View>

            <Text style={S.pieIzq} fixed>{`Generado el ${generado}`}</Text>
            <Text
              style={S.pieDer}
              fixed
              render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`}
            />
          </Page>
        </Document>
      </PDFViewer>
    );
  };

  function handlepdf() {
    if (rows.length === 0) {
      alert("No hay datos para procesar este reporte");
    } else {
      setPresentarpdf(true);
    }
  }

  const exportBtn = (
    <Button
      variant="contained"
      startIcon={<FileDownloadRoundedIcon />}
      disabled={rows.length === 0}
      onClick={handlepdf}
      sx={{
        bgcolor: FP.color.navy,
        color: "#FFFFFF",
        borderRadius: 1.4,
        px: 2.2,
        height: 38,
        boxShadow: "none",
        textTransform: "none",
        fontWeight: 700,
        "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
        "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
      }}
    >
      Export Data
    </Button>
  );

  return (
    <Box component={embedded ? "section" : "main"} sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}>
      {!embedded && (
        <PageHeader
          title="Facturación"
          subtitle="Informe de Motores Facturados (Marca / Modelo / Color)"
          cta={exportBtn}
        />
      )}

      <Box sx={{ p: embedded ? 0 : { xs: 2, sm: 2.5, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Filtros de Búsqueda
            </Typography>
            {embedded && (
              <>
                <Box sx={{ flex: 1 }} />
                {exportBtn}
              </>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Desde"
                  value={fecha1}
                  onChange={(event) => setFecha1(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Hasta"
                  value={fecha2}
                  onChange={(event) => setFecha2(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  options={sucursal || []}
                  value={nosucursal || null}
                  disabled={!veSucursales}
                  getOptionLabel={(opt) => (opt && opt.label) || ""}
                  isOptionEqualToValue={(opt, val) => (opt && val && opt.label) === (val && val.label)}
                  onChange={(event, newValue) => setNosucursal(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              {multiempresa.activo && (
                <Grid item xs={12} sm={6} md={3}>
                  <SelectorEmpresa
                    value={empresaFiltro}
                    onChange={setEmpresaFiltro}
                    empresas={multiempresa.empresas}
                    sx={fpInputSx}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="tipo-venta-label" shrink>
                    Tipo de Venta
                  </InputLabel>
                  <Select
                    labelId="tipo-venta-label"
                    label="Tipo de Venta"
                    notched
                    value={tipoVenta}
                    onChange={(event) => setTipoVenta(event.target.value)}
                  >
                    <MenuItem value="">
                      <em>Todas</em>
                    </MenuItem>
                    <MenuItem value="Contado">Contado</MenuItem>
                    <MenuItem value="Credito">Crédito</MenuItem>
                    <MenuItem value="Refinanciamiento">Refinanciamiento</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="estado-venta-label" shrink>
                    Estado
                  </InputLabel>
                  <Select
                    labelId="estado-venta-label"
                    label="Estado"
                    notched
                    value={estado}
                    onChange={(event) => setEstado(event.target.value)}
                  >
                    <MenuItem value="vigentes">Vigentes</MenuItem>
                    <MenuItem value="anuladas">Anuladas</MenuItem>
                    <MenuItem value="todas">Todas</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  options={marcas || []}
                  value={marca || null}
                  onChange={(event, value) => {
                    const nextMarca = value || "TODO";
                    setMarca(nextMarca);
                    setModelo("TODO");
                    setColore("TODO");
                    const list = (modelos || []).filter((item) => item.name === nextMarca);
                    const modelolist = list.map((item) => item.modelo);
                    modelolist.unshift("TODO");
                    setFilteredModelo(modelolist);
                    if (rowsAll.length > 0) {
                      setRows(applyVehicleFilters(rowsAll, nextMarca, "TODO", "TODO"));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Marca"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  options={filteredModelo || []}
                  value={modelo || null}
                  onChange={(event, value) => {
                    const nextModelo = value || "TODO";
                    setModelo(nextModelo);
                    setColore("TODO");
                    if (rowsAll.length > 0) {
                      setRows(applyVehicleFilters(rowsAll, marca, nextModelo, "TODO"));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Modelo"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  options={colores || []}
                  value={colore || null}
                  onChange={(event, value) => {
                    const nextColore = value || "TODO";
                    setColore(nextColore);
                    if (rowsAll.length > 0) {
                      setRows(applyVehicleFilters(rowsAll, marca, modelo, nextColore));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Color"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => procesarrequerimiento()}
                disabled={loading}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 200 },
                  height: 46,
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontSize: "0.94rem",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: "#CBD5E1", color: "#FFFFFF" },
                }}
              >
                {loading ? "Procesando..." : "Procesar"}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {rows.length > 0 && (
          <Box
            sx={{
              display: "grid",
              width: "100%",
              gap: 2,
              mb: 3,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
              },
            }}
          >
            <StatCard
              label="Total de registros"
              value={String(rows.length)}
              icon={DescriptionIcon}
              accent={FP.color.accentBlue}
            />
            <StatCard
              label="Total inicial"
              value={`$${totalInicial.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={PaymentIcon}
              accent={FP.color.info}
            />
            {completivos && (
              <StatCard
                label="Inicial pagado"
                value={`$${totalInicialPagado.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                icon={PaymentIcon}
                accent={FP.color.successDark}
              />
            )}
            {completivos && (
              <StatCard
                label="Completivo pendiente"
                value={`$${totalCompletivo.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                icon={PaymentIcon}
                accent={FP.color.warning}
              />
            )}
            <StatCard
              label="Total precio"
              value={`$${totalPrecio.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={AttachMoneyIcon}
              accent={FP.color.navy}
            />
            <StatCard
              label="Total financiamiento"
              value={`$${totalFinanciamiento.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={CheckCircleIcon}
              accent={FP.color.successDark}
            />
            <StatCard
              label="Total interés"
              value={`$${totalInteres.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={TrendingUpIcon}
              accent={FP.color.warning}
            />
            <StatCard
              label="Promedio por registro"
              value={`$${(rows.length > 0 ? totalFinanciamiento / rows.length : 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={CalculateOutlinedIcon}
              accent={FP.color.textSecondary}
            />
          </Box>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Listado de motores facturados
            </Typography>
            {rows.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => handlepdf()}
                sx={{
                  borderColor: FP.color.danger,
                  color: FP.color.danger,
                  borderRadius: 1.2,
                  textTransform: "none",
                  fontWeight: 700,
                  "&:hover": { bgcolor: FP.color.dangerSoft, borderColor: FP.color.danger },
                }}
              >
                Generar PDF
              </Button>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
            {rows.length > 0 ? (
              <>
                <Box sx={{ display: { xs: "none", md: "block" }, width: "100%", overflowX: "auto" }}>
                  <DataGrid
                    rows={rows}
                    columns={facturadosColumns}
                    getRowId={(row) => (row.id != null ? String(row.id) : `${row.chasis}-${row.fecha}`)}
                    getRowClassName={({ row }) => (row.anulada ? "fila-anulada" : "")}
                    slots={{ toolbar: FacturadosToolbar }}
                    pageSizeOptions={[5, 10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowHeight={42}
                    sx={{
                      minWidth: { xs: 900, sm: "100%" },
                      height: 520,
                      ...fpDataGridSx,
                      "& .fila-anulada": { color: FP.color.textMuted, bgcolor: "#FFF5F5" },
                    }}
                    autoHeight={false}
                  />
                </Box>
                <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 1.4 }}>
                  {rows.map((row, idx) => (
                    <Box
                      key={row.id != null ? String(row.id) : `card-${idx}`}
                      sx={{
                        border: `1px solid ${FP.color.border}`,
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: "#FFFFFF",
                        boxShadow: FP.shadow.soft,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "1rem", lineHeight: 1.2 }}>
                            Préstamo #{row.id}
                          </Typography>
                          <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.85rem", fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}>
                            {[row.nombres, row.apellidos].filter(Boolean).join(" ") || "—"}
                          </Typography>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.75rem", mt: 0.1 }}>
                            {row.fecha}
                          </Typography>
                          {row.anulada && (
                            <Typography
                              sx={{
                                color: FP.color.danger,
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                mt: 0.25,
                              }}
                            >
                              {notaAnulada(row)}
                              {" · precio original "}
                              {formatMoneyCell(row.precioanulado)}
                            </Typography>
                          )}
                          {[row.marca, row.modelo, row.colore].filter(Boolean).length > 0 && (
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.75rem", mt: 0.25 }}>
                              {[row.marca, row.modelo, row.colore].filter(Boolean).join(" · ")}
                            </Typography>
                          )}
                        </Box>
                        {/* Mismas acciones que la columna del DataGrid: la vista
                            de tarjetas es la que se usa en tablet. */}
                        <Box sx={{ display: "flex", flexShrink: 0 }}>
                          <Tooltip title="Reimprimir factura">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => reimprimirFactura(row)}
                            >
                              <PrintOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {esVentaContado(row) && (
                            <Tooltip title="Documentos de la venta (acto de venta...)">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => abrirDocumentos(row)}
                              >
                                <DescriptionOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {puedeCambiarChasis && (
                            <Tooltip title="Cambiar chasis">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => abrirCambiarChasis(row)}
                              >
                                <PublishedWithChangesOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {puedeBorrar && (
                            <Tooltip title={motivoNoAnulable(row) || "Cancelar factura"}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={!!motivoNoAnulable(row)}
                                  onClick={() => abrirCancelarFactura(row)}
                                >
                                  <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 1 }}>
                        {[
                          ["Precio", row.precio],
                          ["Inicial", row.inicial],
                          ...(completivos
                            ? [
                                ["Inicial pagado", row.inicialpagado],
                                ["Completivo", row.completivo],
                              ]
                            : []),
                          ["Financia", row.financia],
                          ["Int. cobrar", row.interesacobrar],
                        ].map(([label, value]) => (
                          <Box key={label}>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                              {label}
                            </Typography>
                            <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.92rem", fontWeight: 600, fontVariantNumeric: "tabular-nums", wordBreak: "break-word" }}>
                              {value ?? "—"}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      {row.chasis ? (
                        <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${FP.color.border}` }}>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                            Chasis
                          </Typography>
                          <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.82rem", fontWeight: 600, wordBreak: "break-all" }}>
                            {row.chasis}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }} textAlign="center" px={2}>
                  Seleccione los filtros y haga clic en &quot;Procesar&quot; para obtener resultados.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {presentarpdf && (
          <Dialog
            open={presentarpdf}
            maxWidth="lg"
            fullWidth
            fullScreen={isXs}
            onClose={() => setPresentarpdf(false)}
            PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
          >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" fontWeight="bold">
                Vista Previa del Reporte PDF
              </Typography>
              <Button onClick={() => setPresentarpdf(false)} sx={{ minWidth: "auto", p: 1 }}>
                <CancelIcon />
              </Button>
            </DialogTitle>
            <DialogContent>
              <MyDocument rowsData={rows} nombreNegocio={nombreNegocio} />
            </DialogContent>
          </Dialog>
        )}

        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title" sx={{ fontWeight: 700 }}>
            <Box display="flex" alignItems="center">
              <EditIcon sx={{ mr: 1, color: FP.color.accentBlue }} />
              Modificar Chasis
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: FP.color.textSecondary, mb: 1, fontSize: "0.9rem" }} gutterBottom>
              Préstamo #{chasisTarget?.id} — {chasisTarget?.nombres} {chasisTarget?.apellidos}
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, mb: 2, fontSize: "0.9rem" }}>
              Chasis actual: <strong>{chasisTarget?.chasis}</strong>
            </Typography>
            <TextField
              label="Nuevo Chasis"
              variant="outlined"
              fullWidth
              value={chasisnuevo}
              onChange={(event) => setChasisnuevo(event.target.value.toUpperCase())}
              sx={{ mt: 2, ...fpInputSx }}
            />
            <Typography sx={{ color: FP.color.textSecondary, mt: 2, fontSize: "0.8rem" }}>
              El chasis nuevo debe estar libre y en la misma sucursal de la factura. El cambio
              actualiza también el préstamo, la solicitud y el cobro de matrícula.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button
              variant="outlined"
              onClick={() => setOpen(false)}
              disabled={cambiandoChasis}
              startIcon={<CancelIcon />}
              sx={{ textTransform: "none", borderRadius: 1.2 }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={() => modificarchasis()}
              disabled={cambiandoChasis || chasisnuevo.trim().length === 0}
              startIcon={<CheckCircleIcon />}
              sx={{
                textTransform: "none",
                borderRadius: 1.2,
                bgcolor: FP.color.navy,
                "&:hover": { bgcolor: FP.color.navyDeep },
              }}
            >
              {cambiandoChasis ? "Cambiando…" : "Aceptar"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmCancelOpen}
          onClose={() => setConfirmCancelOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
            Cancelar factura
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Seguro que deseas cancelar la factura del chasis{" "}
              <strong>{cancelTarget?.chasis || ""}</strong> (Préstamo #{cancelTarget?.id || ""})?
              Esta acción pondrá precio, financiamiento, inicial, interés y valor de cuota en 0, y
              la factura sale de los informes. Los iniciales que se cobraron para esta venta quedan{" "}
              <strong>anulados</strong> y pasan a Iniciales &gt; Anulados. El préstamo, sus cuotas y
              el chasis no se tocan.
            </DialogContentText>
            <RazonCancelacionField value={razonCancel} onChange={setRazonCancel} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmCancelOpen(false)}>No</Button>
            <Button color="error" variant="contained" onClick={cancelarMotorFacturado}>
              Sí, cancelar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Documentos de una venta al contado: se elige la plantilla y sale
            por impresión/PDF o como Word (mismo diálogo que el contrato de
            la factura x mayor). */}
        <Dialog
          open={!!documentosTarget}
          onClose={() => !generando && setDocumentosTarget(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
            Documentos de la venta #{documentosTarget?.id}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: FP.color.textSecondary, mb: 1, fontSize: "0.9rem" }}>
              {[documentosTarget?.nombres, documentosTarget?.apellidos].filter(Boolean).join(" ")}
              {documentosTarget?.chasis ? ` — ${documentosTarget.chasis}` : ""}
            </Typography>
            {cargandoPlantillas && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {!cargandoPlantillas && plantillasContado.length === 0 && (
              <DialogContentText sx={{ fontSize: "0.9rem" }}>
                No hay plantillas de venta al contado. Cree una en Configuración → Documentos,
                eligiendo &quot;Venta al contado&quot; como origen de los datos.
              </DialogContentText>
            )}
            {!cargandoPlantillas && plantillasContado.length > 0 && (
              <TextField
                select
                fullWidth
                label="Plantilla"
                value={plantillaSel}
                onChange={(e) => setPlantillaSel(e.target.value)}
                sx={{ ...fpInputSx, mt: 1 }}
              >
                {plantillasContado.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.nombre}
                    {p.descripcion ? ` — ${p.descripcion}` : ""}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setDocumentosTarget(null)}
              disabled={!!generando}
              sx={{ textTransform: "none", borderRadius: 1.2 }}
            >
              Cerrar
            </Button>
            <Button
              variant="outlined"
              startIcon={
                generando === "word" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DownloadOutlinedIcon />
                )
              }
              onClick={() => generarDocumento("word")}
              disabled={!!generando || !plantillaSel}
              sx={{ textTransform: "none", borderRadius: 1.2 }}
            >
              Word
            </Button>
            <Button
              variant="contained"
              startIcon={
                generando === "imprimir" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PrintOutlinedIcon />
                )
              }
              onClick={() => generarDocumento("imprimir")}
              disabled={!!generando || !plantillaSel}
              sx={{
                textTransform: "none",
                borderRadius: 1.2,
                bgcolor: FP.color.navy,
                "&:hover": { bgcolor: FP.color.navyDeep },
              }}
            >
              Imprimir
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
export default InformeMotoresFacturadosNuevo;
